import { MapPin, Menu, Moon, Search, Sun, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import { INDEPENDENCE_NOTICE } from '../../../shared/seo/pages.ts';
import { PRIMARY_NAV, SECONDARY_NAV } from '../../app/navigation.ts';
import { InstallButton } from '../InstallButton.tsx';
import { setTheme, useTheme } from '../../lib/theme.ts';
import { Logo } from './Logo.tsx';

function SearchForm() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = query.trim();
    void navigate(q === '' ? '/recherche' : `/recherche?q=${encodeURIComponent(q)}`);
  };
  return (
    <form className="header-search" role="search" onSubmit={submit}>
      <Search size={18} aria-hidden="true" className="header-search__icon" />
      <label className="visually-hidden" htmlFor="header-search-input">
        Rechercher un lieu, un commerce, un événement
      </label>
      <input
        id="header-search-input"
        type="search"
        name="q"
        placeholder="Rechercher un lieu, un événement…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
      />
    </form>
  );
}

/** Deux boutons (clair / sombre) comme sur le mockup : l'état est porté par aria-pressed. */
function ThemeSwitch() {
  const theme = useTheme();
  return (
    <div className="theme-switch" role="group" aria-label="Thème d’affichage">
      <button
        type="button"
        className="icon-button"
        aria-pressed={theme === 'light'}
        onClick={() => setTheme('light')}
        title="Thème clair"
      >
        <Sun size={20} aria-hidden="true" />
        <span className="visually-hidden">Thème clair</span>
      </button>
      <button
        type="button"
        className="icon-button"
        aria-pressed={theme === 'dark'}
        onClick={() => setTheme('dark')}
        title="Thème sombre"
      >
        <Moon size={20} aria-hidden="true" />
        <span className="visually-hidden">Thème sombre</span>
      </button>
    </div>
  );
}

/** Mobile : un seul bouton qui bascule. */
function ThemeToggle() {
  const theme = useTheme();
  const next = theme === 'light' ? 'dark' : 'light';
  return (
    <button type="button" className="icon-button" onClick={() => setTheme(next)}>
      {theme === 'light' ? <Sun size={22} aria-hidden="true" /> : <Moon size={22} aria-hidden="true" />}
      <span className="visually-hidden">{next === 'dark' ? 'Passer au thème sombre' : 'Passer au thème clair'}</span>
    </button>
  );
}

/** Menu mobile : <dialog> natif (piège du focus, Échap, retour du focus assurés par le navigateur). */
function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null || typeof dialog.showModal !== 'function') return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Clic sur le voile (hors du panneau) : fermeture. Le clavier passe par Échap, géré nativement par <dialog>.
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    const onBackdrop = (event: MouseEvent) => {
      if (event.target === dialog) onClose();
    };
    dialog.addEventListener('click', onBackdrop);
    return () => dialog.removeEventListener('click', onBackdrop);
  }, [onClose]);

  return (
    <dialog ref={ref} className="mobile-menu" aria-labelledby="mobile-menu-title" onClose={onClose}>
      <div className="mobile-menu__panel">
        <div className="mobile-menu__header">
          <p id="mobile-menu-title" className="mobile-menu__title">
            <Logo />
          </p>
          <button type="button" className="icon-button" onClick={onClose}>
            <X size={22} aria-hidden="true" />
            <span className="visually-hidden">Fermer le menu</span>
          </button>
        </div>
        <nav aria-label="Menu">
          <ul className="mobile-menu__list">
            {[...PRIMARY_NAV, ...SECONDARY_NAV].map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink to={to} end={end} onClick={onClose}>
                  <Icon size={20} aria-hidden="true" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <InstallButton variant="text" />
        <p className="mobile-menu__notice">{INDEPENDENCE_NOTICE}</p>
      </div>
    </dialog>
  );
}

export function AppHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  // Toute navigation referme le menu (retour arrière compris).
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    setMenuOpen(false);
  }

  return (
    <header className="app-header">
      <div className="app-header__inner container">
        <button
          type="button"
          className="icon-button app-header__menu"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <Menu size={24} aria-hidden="true" />
          <span className="visually-hidden">Ouvrir le menu</span>
        </button>

        <Link to="/" className="app-header__brand" aria-label="Ermont Live, accueil">
          <Logo />
        </Link>

        <nav className="app-header__nav" aria-label="Navigation principale">
          <ul>
            {PRIMARY_NAV.map(({ to, label, end }) => (
              <li key={to}>
                <NavLink to={to} end={end}>
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="app-header__tools">
          <SearchForm />
          <Link to="/recherche" className="icon-button app-header__search-link">
            <Search size={22} aria-hidden="true" />
            <span className="visually-hidden">Rechercher</span>
          </Link>
          <span className="app-header__install">
            <InstallButton variant="icon" />
          </span>
          <span className="app-header__theme-desktop">
            <ThemeSwitch />
          </span>
          <span className="app-header__theme-mobile">
            <ThemeToggle />
          </span>
          <span className="city-pill" title="Ville affichée">
            <MapPin size={16} aria-hidden="true" />
            {DEFAULT_CITY.name}
          </span>
        </div>
      </div>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </header>
  );
}
